import {
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiYAxisChartType,
} from "@incendium/api";
import { Box, Stack } from "@mui/material";
import { AttributionHeader } from "features/analytics";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  CHART_KEY_ATTRIBUTION_HEADER_TOP_BY_TREND,
  CHART_KEY_DASHBOARD_ATTRIBUTION_SALES_BAR,
  CHART_KEY_DASHBOARD_ATTRIBUTION_SPEND_PIE,
  CHART_KEY_DASHBOARD_ATTRIBUTION_ROI_BAR,
} from "features/analytics/constants/chartKeys";
import withPaidChannelAttributes from "HoC/withPaidChannelAttributes";
import { useMemo } from "react";

interface IAttributionOverviewTabProps {
  attributes: ApiChartAttribute[];
  paidChannelAttributes: ApiChartAttribute[];
}

function AttributionOverviewTab({
  attributes,
  paidChannelAttributes,
}: IAttributionOverviewTabProps) {
  const dimension = ApiDimension.DIMENSION_SOURCE;
  const fullAttributes = useMemo(
    () => [...attributes, ...paidChannelAttributes],
    [attributes, paidChannelAttributes],
  );

  return (
    <>
      <Box sx={{ flexShrink: 0, width: "100%", minWidth: 0 }}>
        <AttributionHeader
          chartKey={`${CHART_KEY_ATTRIBUTION_HEADER_TOP_BY_TREND}_dashboard_overview`}
          dimension={dimension}
          title="Marketing Platform"
          attributes={fullAttributes}
        />
      </Box>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ flexShrink: 0, width: "100%", minWidth: 0 }}
      >
        <Box sx={{ minHeight: { xs: 320, md: 400 }, flex: 1, minWidth: 0 }}>
          <StandardAnalyticsCard
            chartKey={CHART_KEY_DASHBOARD_ATTRIBUTION_SPEND_PIE}
            staticAttributes={fullAttributes}
            chart={{
              name: "Total Spend by Platform",
              dimension: [dimension],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.PIE,
              attributes: [],
              includePublisherReadyOnly: true,
              limit: 15,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SPEND],
                },
              ],
              displayOptions: {
                noFilters: true,
                showTotals: true,
              },
            }}
          />
        </Box>

        <Box sx={{ minHeight: { xs: 320, md: 400 }, flex: 1, minWidth: 0 }}>
          <StandardAnalyticsCard
            chartKey={CHART_KEY_DASHBOARD_ATTRIBUTION_SALES_BAR}
            staticAttributes={fullAttributes}
            chart={{
              name: "Sales Count by Platform",
              dimension: [dimension],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.GRAPH,
              attributes: [],
              limit: 15,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SESSION_SALES_COUNT],
                  chart: {
                    [ApiMetric.METRIC_SESSION_SALES_COUNT]:
                      ApiYAxisChartType.BAR,
                  },
                },
              ],
              displayOptions: {
                noFilters: true,
                noLegend: true,
              },
            }}
          />
        </Box>

        <Box sx={{ minHeight: { xs: 320, md: 400 }, flex: 1, minWidth: 0 }}>
          <StandardAnalyticsCard
            chartKey={CHART_KEY_DASHBOARD_ATTRIBUTION_ROI_BAR}
            staticAttributes={fullAttributes}
            chart={{
              name: "ROI by Platform",
              dimension: [dimension],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.GRAPH,
              attributes: [],
              includePublisherReadyOnly: true,
              limit: 15,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_ROI],
                  chart: {
                    [ApiMetric.METRIC_ROI]: ApiYAxisChartType.BAR,
                  },
                },
              ],
              displayOptions: {
                noFilters: true,
                noLegend: true,
              },
            }}
          />
        </Box>
      </Stack>
    </>
  );
}

export default withPaidChannelAttributes(AttributionOverviewTab);
